import { useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { AdminStorefrontDetail } from '@/lib/api/storefrontAdminHooks';
import { cn } from '@/lib/cn';

import {
  changeReference,
  claimCount,
  collectStoreCopy,
  copyKeysChangedSince,
  storeMedia,
  type CopyEntry,
  type StoreMedia,
} from './storeCompliance';
import { Panel, Pill } from './ui';

/**
 * Every patient-facing string the owner wrote, in page order, with the
 * automated flags highlighted (the backend's claims scan, or the screen's
 * mirror of it: storeCompliance.collectStoreCopy). The scan is a keyword
 * aid and never a clearance: the panel never says the copy is clear, no
 * line gets a tick, and Approve always asks the reviewer to confirm they
 * read every line.
 */
export function CopyPanel({ store }: { store: AdminStorefrontDetail }) {
  const entries = collectStoreCopy(store);
  const flags = claimCount(entries);
  const blocking = entries.reduce(
    (n, e) => n + e.claims.filter((c) => c.severity === 'block').length,
    0,
  );
  const media = storeMedia(store);
  const newMedia = media.filter((m) => m.newSinceReview).length;
  const visible = entries.filter((e) => !e.hidden);
  const hidden = entries.filter((e) => e.hidden);
  const [showHidden, setShowHidden] = useState(false);
  // A resubmission (against the last request) or an approved store's
  // edits (against its last review): the rows that changed are marked.
  const reference = changeReference(store);
  const changed = reference ? copyKeysChangedSince(store, reference.since) : new Set<string>();
  const changedLabel = reference?.label ?? '';
  const changedCount = entries.filter((e) => changed.has(e.key)).length;

  return (
    <Panel
      title="Patient-facing copy"
      aside={
        <>
          {changedCount > 0 && (
            <Pill tone="info" className="normal-case">
              {changedCount} changed
            </Pill>
          )}
          {newMedia > 0 && (
            <Pill tone="info">
              {newMedia} new image{newMedia === 1 ? '' : 's'}
            </Pill>
          )}
          <Pill tone={flags > 0 ? 'warning' : 'neutral'} className="normal-case">
            {flags > 0
              ? `${flags} automated flag${flags === 1 ? '' : 's'}${blocking > 0 ? ` · ${blocking} must change` : ''}`
              : 'No automated flags'}
          </Pill>
        </>
      }
      bodyClassName="p-0"
    >
      <div
        className={cn(
          'flex items-start gap-8 px-16 py-10 border-b border-line-subtle type-body-sm text-ink leading-130',
          flags > 0 ? 'bg-warning-wash' : 'bg-raised',
        )}
      >
        <AlertTriangle size={14} className="mt-1 shrink-0 text-warning" aria-hidden />
        <span>
          <strong className="font-normal type-label-xs">Automated flags · always read the copy.</strong>{' '}
          The keyword scan only catches phrases it knows (cure, guarantee, weight-loss amounts,
          anti-aging, FDA approval, before/after…).{' '}
          {flags > 0
            ? '“Must change” is copy an owner save would refuse; “Check” needs your judgment. '
            : 'It found nothing here, which does not mean the copy is compliant. '}
          Read every line below, including hidden sections, for outcome, safety or endorsement
          claims.
        </span>
      </div>
      {media.length > 0 && <MediaStrip media={media} />}
      <dl className="flex flex-col">
        {visible.map((e) => (
          <CopyRow key={e.key} entry={e} changedLabel={changed.has(e.key) ? changedLabel : null} />
        ))}
      </dl>
      {hidden.length > 0 && (
        <div className="border-t border-line-subtle">
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            className="w-full h-36 px-16 text-left type-label-xs text-ink-muted hover:text-ink"
          >
            {showHidden ? 'Hide' : 'Show'} copy in hidden sections ({hidden.length})
          </button>
          {showHidden && (
            <dl className="flex flex-col border-t border-line-subtle bg-raised">
              {hidden.map((e) => (
                <CopyRow key={e.key} entry={e} changedLabel={changed.has(e.key) ? changedLabel : null} />
              ))}
            </dl>
          )}
        </div>
      )}
    </Panel>
  );
}

function CopyRow({ entry, changedLabel }: { entry: CopyEntry; changedLabel: string | null }) {
  const flagged = entry.claims.length > 0;
  const labels = [...new Set(entry.claims.map((c) => c.label))];
  const mustChange = entry.claims.some((c) => c.severity === 'block');
  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-12 px-16 py-10 border-b border-line-subtle last:border-b-0',
        flagged && 'bg-[#FFFBEB]',
      )}
    >
      <dt className="sm:w-[14rem] shrink-0 type-label-xs text-ink-muted pt-2 flex items-center gap-6">
        {flagged && <AlertTriangle size={12} className="text-warning shrink-0" aria-hidden />}
        {entry.label}
      </dt>
      <dd className="flex flex-col gap-4 min-w-0">
        {changedLabel && (
          <span>
            <Pill tone="info" className="normal-case">
              {changedLabel}
            </Pill>
          </span>
        )}
        <p className="type-body-sm text-ink whitespace-pre-wrap break-words leading-130">
          {highlight(entry)}
        </p>
        {flagged && (
          <span className="flex flex-wrap items-center gap-6 type-body-sm text-warning">
            <Pill tone={mustChange ? 'danger' : 'warning'} className="normal-case">
              {mustChange ? 'Must change' : 'Check'}
            </Pill>
            {labels.join(' · ')}
          </span>
        )}
      </dd>
    </div>
  );
}

function highlight(entry: CopyEntry): ReactNode {
  if (entry.claims.length === 0) return entry.text;
  const parts: ReactNode[] = [];
  let at = 0;
  entry.claims.forEach((c, i) => {
    if (c.start > at) parts.push(entry.text.slice(at, c.start));
    parts.push(
      <mark
        key={i}
        title={`${c.label} (${c.severity === 'block' ? 'must change' : 'check'})`}
        className={cn('text-ink px-2', c.severity === 'block' ? 'bg-[#FECACA]' : 'bg-[#FDE68A]')}
      >
        {entry.text.slice(c.start, c.end)}
      </mark>,
    );
    at = c.end;
  });
  if (at < entry.text.length) parts.push(entry.text.slice(at));
  return parts;
}

/** The owner's own images (logo, hero, lifestyle and about photos), as
 *  patients see them: the page shows a section photo instead of the
 *  template's stock image. New ones since the last review are marked. */
function MediaStrip({ media }: { media: StoreMedia[] }) {
  return (
    <div className="flex flex-col gap-10 px-16 py-12 border-b border-line-subtle">
      <span className="type-label-xs text-ink-muted">
        Owner images · check for before/after or results imagery, white coats and clinical
        settings
      </span>
      <ul className="flex flex-wrap gap-12">
        {media.map((m) => (
          <li key={m.key} className="flex flex-col gap-6 w-[14rem]">
            <a
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'block border bg-sunken overflow-hidden',
                m.newSinceReview ? 'border-info' : 'border-line-subtle',
              )}
              title="Open full size"
            >
              <img
                src={m.url}
                alt={m.label}
                loading="lazy"
                className={cn(
                  'w-full h-[10rem]',
                  m.key === 'logo' ? 'object-contain p-12 bg-white' : 'object-cover',
                )}
              />
            </a>
            <span className="flex flex-wrap items-center gap-6 type-body-sm text-ink">
              {m.label}
              {m.newSinceReview && <Pill tone="info">New since review</Pill>}
              {m.hidden && <Pill>Hidden section</Pill>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
